import React, { useRef, useEffect, useState } from 'react'
import Talk from 'talkjs'
import audioSound from '../mp3/chat-notification.mp3'
import { CONFIG } from '../../../Config/config'
import SelfServeClientChatView from '../component'
import { localStore } from '../../../Module/Browser/LocalStorage'
import { useIdleTimer } from 'react-idle-timer/dist/index.legacy.cjs.js'
import { userMessages } from '../userMessages'
import { usePostHog } from 'posthog-js/react'

const { appID } = CONFIG.TALKJS

const SelfServeClientChat = ({
  user,
  countryId,
  client,
  toggleAssistant,
  openChat = false,
}) => {
  const [isOpen, setIsOpen] = useState(openChat)

  let ref = useRef(null)
  const isInitialized = useRef(false)
  const sessionRef = useRef(null)

  const playNotificationSound = () => {
    const audio = new Audio(audioSound)

    audio.play().catch(error => console.error('Error playing the audio', error))
  }
  const conversationRef = useRef(null)
  const posthogTrack = usePostHog()

  const initChat = async ({ ref, user }) => {
    if (isInitialized.current || sessionRef.current) {
      return
    }

    try {
      await Talk.ready
    } catch (error) {
      console.error('Error waiting for TalkJS ready:', error)
      return
    }

    const session = new Talk.Session({
      appId: appID,
      me: new Talk.User({
        id: user.id,
        name: user.firstName,
        email: user.email,
        photoUrl: user.photoUrl,
      }),
    })

    sessionRef.current = session

    session.on('message', message => {
      if (message.sender.id !== session.me.id) {
        playNotificationSound()
      }
    })

    const conversationId = Talk.oneOnOneId(session.me, session.me)

    const conversationIdWithCategory = `client-oads:${conversationId}`

    const conversation = session.getOrCreateConversation(
      conversationIdWithCategory,
    )

    conversation.setParticipant(session.me)

    conversation.setAttributes({
      welcomeMessages: [
        `Hey ${
          user.firstName
        }, thanks for chatting with Promofy, I'm here to help!`,
        'How can I help you today?',
      ],
      custom: {
        isClientChat: 'true',
        countryId,
      },
      subject: `${client.name} - ${user.firstName} ${user.lastName}`,
    })
    const chatbox = session.createChatbox({
      captureKeyboardEvents: true,
      theme: 'oads',
    })

    chatbox.select(conversation)

    chatbox.mount(ref)
    conversationRef.current = conversation
    isInitialized.current = true
  }

  const sendUserMessage = event => {
    const message = userMessages[event.type]
    const detail = event.detail

    if (message && event) {
      conversationRef.current.sendMessage(
        message({
          location: detail.location,
          channel: detail.channel,
          design: detail.design,
          result: detail.result,
        }),
      )
    }
  }

  const autoOpenChat = () => {
    const hasAutoClosedBefore = localStore.getData(CONFIG.STORE.CHAT_AUTO_OPEN)

    if (hasAutoClosedBefore != 'true') {
      posthogTrack.capture('Opened Assistant Automatically', {
        experiment: 'assistant_v1.5',
      })
      setIsOpen(true)
      localStore.setData(CONFIG.STORE.CHAT_AUTO_OPEN, 'true')
    }
  }

  useIdleTimer({
    onIdle: autoOpenChat,
    timeout: 20000,
    throttle: 500,
  })

  useEffect(() => {
    if (user && !isInitialized.current) {
      initChat({
        ref,
        user,
      })
    }

    document.addEventListener('campaign:chat_expert', event => {
      setIsOpen(true)
      sendUserMessage(event)
    })

    document.addEventListener('campaign:create', event => {
      setIsOpen(true)
      sendUserMessage(event)
      localStorage.removeItem('oppizi_assistantValues')
      localStorage.removeItem('oppizi_assistantActiveTab')
    })

    return () => {
      document.removeEventListener('campaign:chat_expert', () => {})
      document.removeEventListener('campaign:create', () => {})
    }
  }, [user])

  const setRef = value => {
    ref = value
  }

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        try {
          sessionRef.current.destroy()
          sessionRef.current = null
          isInitialized.current = false
        } catch (error) {
          console.error('Error destroying TalkJS session:', error)
        }
      }
    }
  }, [])

  return (
    <SelfServeClientChatView
      setRef={setRef}
      user={user}
      toggleAssistant={toggleAssistant}
      countryId={countryId}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  )
}
export default SelfServeClientChat
